import { useState } from 'react';
import {
  Box,
  VStack,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from '@chakra-ui/react';
import { KModal, KText } from '@/ui-library/components';

const MIN_MINUTES = 1;
const MAX_MINUTES = 15;
const DEFAULT_MINUTES = 5;

interface DemoInterviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStart: (durationMin: number) => void;
  isStarting?: boolean;
}

// Dev/staging-only modal: asks for a round duration, then kicks off the simulated-interview harness (two
// same-named bots hold the interview while the real coach runs). Everything else is derived server-side.
export function DemoInterviewModal({
  isOpen,
  onClose,
  onStart,
  isStarting,
}: DemoInterviewModalProps) {
  const [duration, setDuration] = useState<number>(DEFAULT_MINUTES);
  const clamped = Math.min(
    MAX_MINUTES,
    Math.max(MIN_MINUTES, Math.round(duration || DEFAULT_MINUTES))
  );

  return (
    <KModal
      isOpen={isOpen}
      onClose={onClose}
      title="Run demo interview"
      subHeaderText="Simulate this interview with two bots so you can showcase the live coach — no real candidate needed."
      ctaLabel="Start demo"
      ctaAction={() => onStart(clamped)}
      isCtaLoading={isStarting}
      ctaButtonProps={{ loadingText: 'Starting…' }}
    >
      <VStack align="stretch" spacing="16px" pb="8px">
        <Box>
          <KText size="sm-medium" color="secondary-900" mb="6px">
            Duration (minutes)
          </KText>
          <NumberInput
            value={duration}
            min={MIN_MINUTES}
            max={MAX_MINUTES}
            onChange={(_, valueAsNumber) =>
              setDuration(
                Number.isNaN(valueAsNumber) ? DEFAULT_MINUTES : valueAsNumber
              )
            }
            w="130px"
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <KText size="xs-regular" color="secondary-500" mt="6px">
            {MIN_MINUTES}–{MAX_MINUTES} minutes. The coach compresses its plan
            to fit.
          </KText>
        </Box>

        <Box bg="secondary-50" borderRadius="8px" p="12px 14px">
          <KText size="xs-medium" color="secondary-700" mb="8px">
            After you start:
          </KText>
          <VStack align="stretch" spacing="5px">
            <KText size="xs-regular" color="secondary-600">
              1. Open the Google Meet (you&rsquo;re the host).
            </KText>
            <KText size="xs-regular" color="secondary-600">
              2. Admit the 3 Kula bots from the waiting room.
            </KText>
            <KText size="xs-regular" color="secondary-600">
              3. Optionally share your screen — the coach&rsquo;s vision picks
              it up.
            </KText>
          </VStack>
        </Box>
      </VStack>
    </KModal>
  );
}
